import apiClient, { BASE_URL } from '../config/api'
import { exchangeRateDB } from '../db/indexedDB'

/**
 * Fetches exchange rate from API and stores it offline
 * @returns {Promise<number|null>} The exchange rate or null if fetch fails
 */
export const fetchAndStoreExchangeRate = async () => {
  try {
    // Only fetch if online
    if (!navigator.onLine) {
      console.log('[EXCHANGE RATE] Device is offline, using cached rate')
      const cached = await exchangeRateDB.get()
      return cached?.current_rate || null
    }

    const response = await apiClient.get(`${BASE_URL}/api/sales/exchange-rate/`)
    const rate = response.data?.current_rate || response.data?.rate || null
    
    if (rate && !isNaN(parseFloat(rate))) {
      await exchangeRateDB.save({ current_rate: parseFloat(rate) })
      console.log('[EXCHANGE RATE] ✅ Fetched and stored exchange rate:', rate)
      return parseFloat(rate)
    }
    
    return null
  } catch (error) {
    console.error('[EXCHANGE RATE] Error fetching exchange rate:', error)
    
    // If offline or error, try to use cached rate
    try {
      const cached = await exchangeRateDB.get()
      if (cached?.current_rate) {
        console.log('[EXCHANGE RATE] Using cached exchange rate:', cached.current_rate)
        return cached.current_rate
      }
    } catch (cacheError) {
      console.error('[EXCHANGE RATE] Error reading cached rate:', cacheError)
    }
    
    return null
  }
}

/**
 * Gets exchange rate from cache (offline-first)
 * @returns {Promise<number|null>} The exchange rate or null if not available
 */
export const getExchangeRate = async () => {
  try {
    const cached = await exchangeRateDB.get()
    return cached?.current_rate || null
  } catch (error) {
    console.error('[EXCHANGE RATE] Error getting exchange rate:', error)
    return null
  }
}

/**
 * Checks if payment method is in ZIG
 * @param {string} paymentMethod - The payment method
 * @returns {boolean} True if payment is in ZIG
 */
export const isZigPayment = (paymentMethod) => {
  return paymentMethod === 'zig_cash' || 
         paymentMethod === 'ecocash_zig' || 
         paymentMethod === 'bank_transfer_zig'
}

/**
 * Checks if payment method is in USD
 * @param {string} paymentMethod - The payment method
 * @returns {boolean} True if payment is in USD
 */
export const isUsdPayment = (paymentMethod) => {
  return paymentMethod === 'usd_cash' || 
         paymentMethod === 'ecocash_usd' || 
         paymentMethod === 'bank_transfer_usd'
}

/**
 * Formats payment method name for display
 * @param {string} paymentMethod - The payment method code
 * @returns {string} Formatted payment method name
 */
export const formatPaymentMethod = (paymentMethod) => {
  const paymentNames = {
    'usd_cash': 'USD Cash',
    'zig_cash': 'ZIG Cash',
    'ecocash_usd': 'Ecocash USD',
    'ecocash_zig': 'Ecocash ZIG',
    'bank_transfer_zig': 'Bank Transfer ZIG',
    'bank_transfer_usd': 'Bank Transfer USD',
  }
  return paymentNames[paymentMethod] || paymentMethod.toUpperCase()
}

/**
 * Handles exchange rate update when network is restored
 * This is called automatically when the device comes back online
 */
const handleOnlineExchangeRateUpdate = async () => {
  try {
    console.log('[EXCHANGE RATE] 🌐 Network restored, fetching latest exchange rate...')
    const rate = await fetchAndStoreExchangeRate()
    if (rate) {
      console.log('[EXCHANGE RATE] ✅ Exchange rate updated on network restore:', rate)
      
      // Dispatch a custom event to notify components that the rate was updated
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('exchangeRateUpdated', { detail: { rate } }))
      }
    } else {
      console.warn('[EXCHANGE RATE] ⚠️ Failed to fetch exchange rate on network restore')
    }
  } catch (error) {
    console.error('[EXCHANGE RATE] ❌ Error updating exchange rate on network restore:', error)
  }
}

/**
 * Registers global network restore listener for exchange rate updates
 * This ensures the exchange rate is always kept up-to-date when network is restored
 * Similar to token recovery pattern in api.js
 */
const registerExchangeRateNetworkListener = () => {
  if (typeof window === 'undefined') return
  
  // Prevent multiple registrations (HMR protection)
  const LISTENER_KEY = '__wealthbag_exchange_rate_listener_registered'
  if (window[LISTENER_KEY]) return
  
  window[LISTENER_KEY] = true
  
  // Listen for online event
  window.addEventListener('online', handleOnlineExchangeRateUpdate)
  
  // Also check on initial load if already online
  if (typeof navigator !== 'undefined' && navigator.onLine) {
    // Small delay to ensure app is fully initialized
    setTimeout(() => {
      handleOnlineExchangeRateUpdate()
    }, 1000)
  }
  
  console.log('[EXCHANGE RATE] 📡 Global network restore listener registered')
}

// Auto-register the listener when this module is imported
registerExchangeRateNetworkListener()

